import type { TimezoneListDto, UpdateTimezoneResponseDto } from '@nomiss/sdk';
import {
  ApiRequestError,
  executeSdkRawRequest,
} from './api-request.util';
import { usersApi } from './sdk-client';

export type TimezoneItem = {
  id: string;
  iana: string;
  displayName: string;
  offsetLabel: string;
  observesDst: boolean;
  isDefault: boolean;
};

export type TimezoneOptionsResponse = {
  selectedId: string;
  defaultId: string | null;
  items: TimezoneItem[];
};

export class TimezoneError extends ApiRequestError {
  constructor(status: number, message: string) {
    super(status, message, 'TimezoneError');
  }
}

function normalizeTimezoneList(payload: TimezoneListDto): TimezoneOptionsResponse {
  return {
    selectedId: payload.selectedId,
    defaultId:
      typeof payload.defaultId === 'string' ? payload.defaultId : null,
    items: (payload.items ?? []).map(item => ({
      id: item.id,
      iana: item.iana,
      displayName: item.displayName,
      offsetLabel: item.offsetLabel,
      observesDst: item.observesDst,
      isDefault: item.isDefault,
    })),
  };
}

export async function getTimezoneOptions(
  query?: string,
  limit = 100,
): Promise<TimezoneOptionsResponse> {
  const data = await executeSdkRawRequest<TimezoneListDto>({
    request: () =>
      usersApi.timezoneControllerOptionsV1Raw({
        q: query,
        limit,
      }),
    fallback: 'Failed to load timezones',
    ErrorClass: TimezoneError,
  });

  return normalizeTimezoneList(data);
}

export async function updateTimezone(timezoneId: string): Promise<TimezoneItem> {
  const data = await executeSdkRawRequest<UpdateTimezoneResponseDto>({
    request: () =>
      usersApi.timezoneControllerUpdateV1Raw({
        updateTimezoneDto: { timezoneId },
      }),
    fallback: 'Failed to update timezone',
    ErrorClass: TimezoneError,
  });

  return {
    id: data.selectedId,
    iana: '',
    displayName: data.displayName,
    offsetLabel: data.offsetLabel,
    observesDst: false,
    isDefault: false,
  };
}

export function findSelectedTimezoneLabel(
  response: TimezoneOptionsResponse,
): string | null {
  const selected = response.items.find(item => item.id === response.selectedId);
  if (!selected) {
    return null;
  }

  return `${selected.displayName} (${selected.offsetLabel})`;
}
